import { Component, OnInit, Input, ElementRef, ViewChild, OnDestroy, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { ImmediatelyErrorStateMatcher } from '../../components/immediately-error-state-matcher.component';
import { SelectBox } from '../../models/selectBox.model';
import { ZyuushoDataApi } from '../../models/zyuushoDataApi';
import { ZyuushoInfo } from '../../models/zyuushoInfo.model';
import { UsedSzza001aService } from '../../services/used-szza001a.service';
import { Szza001aResponse, ResponseCommon } from 'sg-api';

/**
 * 住所共通
 * プレゼンテーショナルコンポーネント内で住所の入力が必要な場合に、本コンポーネントを取り込む。
 * @export
 * @class ZyuushoApiComponent
 * @implements {OnInit}
 * @implements {OnChanges}
 */
@Component({
  selector: 'app-zyuusho-api',
  templateUrl: './zyuusho-api.component.html',
  styleUrls: ['./zyuusho-api.component.scss']
})
export class ZyuushoApiComponent implements OnInit, OnDestroy {
  /** 入力：取り込み先のFormGroup */
  @Input() parentFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  /** 入力：追加Form名（APIに合わせた名前を設定する） */
  @Input() zyuushoInfo: ZyuushoInfo = {
    addrcd: { formControlName: 'visitAddrcd', labelName: '住所選択リスト', required: false },
    postno: { formControlName: 'visitPostno', labelName: '郵便番号', required: false },
    banti: { formControlName: 'visitPosvisitBantitno', labelName: '番地', required: false },
    bantiDiv: { formControlName: 'visitBntdiv', labelName: '番地区分', required: false },
    gou: { formControlName: 'visitGou', labelName: '号', required: false },
    gouDiv: { formControlName: 'visitGouDiv', labelName: '号区分', required: false },
    bunptGou: { formControlName: 'visitBunptGou', labelName: '分筆号', required: false },
    supadrAname: { formControlName: 'visitSupadrAname', labelName: '住宅・棟号漢字名', required: false },
    supadrKananm: { formControlName: 'visitSupadrKananm', labelName: '住宅・棟号カナ名', required: false },
    yagouAname: { formControlName: 'visitYagouAname', labelName: '屋号・業種漢字名', required: false },
    yagouKananm: { formControlName: 'visitYagouKananm', labelName: '屋号・業種カナ名', required: false },
  };

  /** 入力：郵便番号をFormGroupに追加するか（true:追加） NOTE:総合契約構成情報等、入力用住所をリストで管理する時用 */
  @Input() addPostNo: boolean = false;

  /** 出力：住所選択リスト確定時に、住所選択リストの表示名称を返却する */
  @Output() zyuusho = new EventEmitter<string>();

  /** ドロップダウンを制御するためのコントロール */
  @ViewChild('selectZyuushoCode', { static: false }) selectZyuushoCode;
  /** コントロールが無効な状態となったタイミングで即座にエラーを検知するための定義 */
  matcher: ImmediatelyErrorStateMatcher = new ImmediatelyErrorStateMatcher();
  /** 住所コード検索エラーメッセージ */
  errorMessage: string = '';
  /** 住所情報取得結果 */
  response: Szza001aResponse;
  /** responseCommon */
  responseCommon: ResponseCommon;
  /** 住所リスト */
  addrcdList: SelectBox[] = [];
  /** 住所コード検索用Form */
  searchFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  /** 住所コード */
  addrcd: UntypedFormControl = new UntypedFormControl({ value: '', disabled: true });
  /** 郵便番号（FormGroupには追加しない） */
  postno: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 番地 */
  banti: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 番地区分 */
  bantiDiv: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 号 */
  gou: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 号区分 */
  gouDiv: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 分筆号 */
  bunptGou: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 住宅・棟号漢字名 */
  supadrAname: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 住宅・棟号カナ名 */
  supadrKananm: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 屋号・業種漢字名 */
  yagouAname: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });
  /** 屋号・業種カナ名 */
  yagouKananm: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false });

  /** 郵便番号の設定先FormGroupの参照 */
  postnoFgRef: UntypedFormGroup | null = null;

  /** 番地区分リスト */
  bantiDivList: SelectBox[] = [];
  /** 号区分リスト */
  gouDivList: SelectBox[] = [];


  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {FormBuilder} fb
   * @param {SZZA001AService} sZZA001AService
   * @param {ElementRef} el
   * @memberof ZyuushoComponent
   */
  constructor(private fb: UntypedFormBuilder, private el: ElementRef, private usedSzza001aService: UsedSzza001aService) {
  }

  /**
   * 画面起動時処理
   * @memberof ZyuushoComponent
   */
  ngOnInit(): void {
    // 親コンポーネントのFormGroupに、住所のFormGroupを追加
    this.parentFormGroup.addControl(this.zyuushoInfo.addrcd.formControlName, this.addrcd);
    this.parentFormGroup.addControl(this.zyuushoInfo.banti.formControlName, this.banti);
    this.parentFormGroup.addControl(this.zyuushoInfo.bantiDiv.formControlName, this.bantiDiv);
    this.parentFormGroup.addControl(this.zyuushoInfo.gou.formControlName, this.gou);
    this.parentFormGroup.addControl(this.zyuushoInfo.gouDiv.formControlName, this.gouDiv);
    this.parentFormGroup.addControl(this.zyuushoInfo.bunptGou.formControlName, this.bunptGou);
    this.parentFormGroup.addControl(this.zyuushoInfo.supadrAname.formControlName, this.supadrAname);
    this.parentFormGroup.addControl(this.zyuushoInfo.supadrKananm.formControlName, this.supadrKananm);
    this.parentFormGroup.addControl(this.zyuushoInfo.yagouAname.formControlName, this.yagouAname);
    this.parentFormGroup.addControl(this.zyuushoInfo.yagouKananm.formControlName, this.yagouKananm);

    // 郵便番号を設定
    if (this.addPostNo) {
      this.parentFormGroup.addControl(this.zyuushoInfo.postno.formControlName, this.postno);
      this.postnoFgRef = this.parentFormGroup;
    }
    else {
      // 住所コード検索用Formに設定
      this.searchFormGroup.addControl(this.zyuushoInfo.postno.formControlName, this.postno);
      this.postnoFgRef = this.searchFormGroup;
    }

    // 必須設定
    Object.keys(this.zyuushoInfo).forEach(key => {
      if (this.zyuushoInfo[key].required == true) {
        if (key == 'postno') {
          // 郵便番号は、親Formにセットしないため、個別に設定
          this.postno.setValidators(Validators.required);
          return;
        }
        this.parentFormGroup.controls[this.zyuushoInfo[key].formControlName].setValidators(Validators.required);
        this.parentFormGroup.controls[this.zyuushoInfo[key].formControlName].updateValueAndValidity();
      }
    });

    // 番地、号区分を（空白値）選択状態とする
    if (this.bantiDivList.length != 0) {
      this.bantiDiv.setValue('0');
    }
    if (this.gouDivList.length != 0) {
      this.gouDiv.setValue('0');
    }
  }

  /**
   * デストロイ処理
   *
   * @memberof ZyuushoApiComponent
   */
  ngOnDestroy(): void {
    this.parentFormGroup.removeControl(this.zyuushoInfo.addrcd.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.banti.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.bantiDiv.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.gou.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.gouDiv.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.bunptGou.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.supadrAname.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.supadrKananm.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.yagouAname.formControlName);
    this.parentFormGroup.removeControl(this.zyuushoInfo.yagouKananm.formControlName);
    // removeControlは、指定された名称の有無で動作に影響がないため、郵便番号を一括で削除
    this.parentFormGroup.removeControl(this.zyuushoInfo.postno.formControlName);
  }

  /**
   * 画面項目に、親コンポーネントから渡された初期値を設定
   * @param {ZyuushoData} ZyuushoDataApi
   * @memberof ZyuushoComponent
   */
  public setInitalValue(zyuushoDataApi: ZyuushoDataApi, searchSkipFlg?: boolean): void {
    this.parentFormGroup.controls[this.zyuushoInfo.banti.formControlName].setValue(zyuushoDataApi.banti);
    this.parentFormGroup.controls[this.zyuushoInfo.bantiDiv.formControlName].setValue(zyuushoDataApi.bantiDiv);
    this.parentFormGroup.controls[this.zyuushoInfo.gou.formControlName].setValue(zyuushoDataApi.gou);
    this.parentFormGroup.controls[this.zyuushoInfo.gouDiv.formControlName].setValue(zyuushoDataApi.gouDiv);
    this.parentFormGroup.controls[this.zyuushoInfo.bunptGou.formControlName].setValue(zyuushoDataApi.bunptGou);
    this.parentFormGroup.controls[this.zyuushoInfo.supadrAname.formControlName].setValue(zyuushoDataApi.supadrAname);
    this.parentFormGroup.controls[this.zyuushoInfo.supadrKananm.formControlName].setValue(zyuushoDataApi.supadrKananm);
    this.parentFormGroup.controls[this.zyuushoInfo.yagouAname.formControlName].setValue(zyuushoDataApi.yagouAname);
    this.parentFormGroup.controls[this.zyuushoInfo.yagouKananm.formControlName].setValue(zyuushoDataApi.yagouKananm);

    this.postnoFgRef.controls[this.zyuushoInfo.postno.formControlName].setValue(zyuushoDataApi.postno);

    if (searchSkipFlg != null && searchSkipFlg == true) {
      this.parentFormGroup.controls[this.zyuushoInfo.addrcd.formControlName].setValue(zyuushoDataApi.addrcd);
      this.parentFormGroup.controls[this.zyuushoInfo.addrcd.formControlName].disable();
      this.addrcdList = [];
      return;
    }

    // 住所コードリスト
    this.searchZyuushoCode(this.postnoFgRef.controls[this.zyuushoInfo.postno.formControlName].value, () => {
      // 住所コードリストから現在の住所コードを選択
      this.parentFormGroup.controls[this.zyuushoInfo.addrcd.formControlName].setValue(zyuushoDataApi.addrcd);
    });
  }

  /**
   * 検索ボタン押下時処理
   * @param {*} event
   * @memberof ZyuushoComponent
   */
  onClickZyuushoSearch(): void {
    // 郵便番号から住所コードを取得し、住所リストに表示
    this.searchZyuushoCode(this.postnoFgRef.controls[this.zyuushoInfo.postno.formControlName].value, () => {
      // 住所選択プルダウンをオープン
      this.selectZyuushoCode.open();
    });
  }

  /**
   * 住所情報取得を実行
   * @param {string} zipCode
   * @param {() => void} callback
   * @memberof ZyuushoComponent
   */
  searchZyuushoCode(zipCode: string, callback: () => void): void {
    this.errorMessage = '';

    // APIを実行し住所を取得
    this.usedSzza001aService.searchZyuushoCode(zipCode).subscribe(response => {
      this.response = response;
      this.responseCommon = response.responseCommon;
      let addList: SelectBox[] = [];
      if (response.addressSearchInfoToZipCodeDtoList != null) {
        for (let dto of response.addressSearchInfoToZipCodeDtoList) {
          let editedValue: string = '';
          if (dto.prefecturesName) editedValue = dto.prefecturesName;
          if (dto.cityName) editedValue += dto.cityName;
          if (dto.townAreaName) editedValue += dto.townAreaName;
          if (dto.ooazaName) editedValue += dto.ooazaName;
          let data: SelectBox = {
            value: dto.addressCode,
            viewValue: editedValue
          }
          addList.push(data);
        }
        this.addrcdList = addList;
        this.parentFormGroup.controls[this.zyuushoInfo.addrcd.formControlName].enable();

        callback();
      }
    },
      error => {
        this.errorMessage = '住所の取得に失敗しました'
      }
    );
  }

  /**
   * 住所選択リスト変更時処理
   *
   * @param {string} codeValue
   * @memberof ZyuushoApiComponent
   */
  selectAddrCode(codeValue: string): void {
    this.zyuusho.emit(this.getDictionaryText(this.addrcdList, codeValue));
  }

  /**
   * 現在設定されている住所の値を組み合わせた文字列として返却する
   * @returns 
   */
  public getDisplayZyuusho(): string {
    // 表示用住所
    const zyuusho = this.getDictionaryText(this.addrcdList, this.parentFormGroup.get(this.zyuushoInfo.addrcd.formControlName).value);
    /** 番地 */
    const banti = this.parentFormGroup.get(this.zyuushoInfo.banti.formControlName).value;
    /** 番地区分 */
    const bantiDiv = this.getDictionaryText(this.bantiDivList, this.parentFormGroup.get(this.zyuushoInfo.bantiDiv.formControlName).value);
    /** 号 */
    const gou = this.parentFormGroup.get(this.zyuushoInfo.gou.formControlName).value;
    /** 号区分 */
    const gouDiv = this.getDictionaryText(this.gouDivList, this.parentFormGroup.get(this.zyuushoInfo.gouDiv.formControlName).value);
    /** 分筆号 */
    const bunptGou = this.parentFormGroup.get(this.zyuushoInfo.bunptGou.formControlName).value;
    /** 住宅・棟号漢字名 */
    const supadrAname = this.parentFormGroup.get(this.zyuushoInfo.supadrAname.formControlName).value;
    /** 屋号・業種漢字名 */
    const yagouAname = this.parentFormGroup.get(this.zyuushoInfo.yagouAname.formControlName).value;

    let displayZyuusho = zyuusho + banti + bantiDiv + gou + gouDiv + bunptGou + supadrAname + yagouAname;

    return displayZyuusho;
  }

  /**
   * コード変換で取得したSelectBoxリストから、対象の表示値を取得する
   * @static
   * @param {SelectBox[]} list
   * @param {string} value
   * @return {*}  {string}
   * @memberof ZyuushoApiComponent
   */
  private getDictionaryText(list: SelectBox[], value: string): string {
    for (let i = 0; i < list.length; i++) {
      if (list[i].value == value) {
        return list[i].viewValue;
      }
    }
    return "";
  }
}
